import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { consumeImpersonationHash, getImpToken } from "@/lib/impersonation";

/**
 * Module 6D Phase 3 — Impersonation launch landing page.
 *
 * URL: `/impersonate/launch#imp_token=imp_xxx&studio_id=…&studio_label=…
 *                          &expires_at=…&admin_email=…`
 *
 * Behavior:
 *   1. Synchronously read + consume the URL hash on first paint.
 *   2. Strip the hash from the address bar (history.replaceState).
 *   3. Redirect to `/dashboard` — the impersonated studio's home. The
 *      banner from `ImpersonationBanner` is mounted globally in Layout
 *      so it shows up immediately.
 *   4. If no valid imp_token in the hash AND none already in
 *      sessionStorage, show an error message (and a link back to
 *      `/admin`).
 */
export default function ImpersonationLaunch() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);

  useEffect(() => {
    const consumed = consumeImpersonationHash();
    if (consumed || getImpToken()) {
      // Hard navigation so AuthProvider re-runs `/me` with the imp_token
      // header (and the impersonated studio loads fresh).
      window.location.replace("/dashboard");
      return;
    }
    setError("No impersonation token found in URL.");
  }, [navigate]);

  if (!error) {
    return (
      <div
        className="min-h-screen w-full flex items-center justify-center"
        style={{ backgroundColor: "var(--brand-paper)" }}
        data-testid="impersonation-launch-screen"
      >
        <div className="kicker">LOADING SUPPORT SESSION</div>
      </div>
    );
  }
  return (
    <div
      className="min-h-screen w-full flex items-center justify-center px-6"
      style={{ backgroundColor: "var(--brand-paper)" }}
      data-testid="impersonation-launch-error"
    >
      <div className="max-w-md text-center">
        <div className="kicker mb-3" style={{ color: "var(--brand-danger, #b91c1c)" }}>
          IMPERSONATION FAILED
        </div>
        <p className="text-base mb-4" style={{ color: "var(--brand-ink)" }}>
          {error}
        </p>
        <a
          href="/admin"
          className="underline text-sm"
          data-testid="impersonation-launch-back-link"
        >
          Back to admin shell
        </a>
      </div>
    </div>
  );
}
